"use client"

import { useState } from "react"
import { History } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  PromptHistoryDialog,
  type PromptVersionEntry,
} from "@/components/agents/shared/prompt-history-dialog"
import {
  useAgentPromptVersionsQuery,
  type AgentPromptScopeType,
  type AgentPromptVersionView,
} from "@/hooks/queries/agent-prompt-versions"
import type { PromptVersionSource } from "@/lib/ai/prompt-versions"

/** Provenance is an agent-prompt concern; the dialog only renders the label it is handed. */
const SOURCE_LABELS: Record<PromptVersionSource, string> = {
  manual: "Edited",
  role_apply: "Role applied",
  quality_suggestion: "Suggestion you applied",
  quality_automatic: "Improved automatically",
  quality_undo: "Change undone",
}

interface PromptHistoryButtonProps {
  scopeType: AgentPromptScopeType
  scopeId: string
  useCase: string
  currentPrompt: string
  onRestore: (systemPrompt: string) => void
  /** False for view-only callers: they cannot save what a restore would load. */
  canRestore: boolean
  /** A caller's label, note and action for a version, e.g. the change a version applied. */
  describeVersion?: (version: AgentPromptVersionView) => Pick<PromptVersionEntry, "label" | "note" | "action">
}

/**
 * Trigger + dialog for one prompt editor; owns the open state and the query so callers do not
 * repeat either. The dialog itself is source-agnostic — see the tool instructions dialog for
 * the other caller.
 */
export function PromptHistoryButton({
  scopeType,
  scopeId,
  useCase,
  currentPrompt,
  onRestore,
  canRestore,
  describeVersion,
}: PromptHistoryButtonProps) {
  const [open, setOpen] = useState(false)
  const versionsQuery = useAgentPromptVersionsQuery(scopeType, scopeId, useCase, open)

  const entries: PromptVersionEntry[] = (versionsQuery.data ?? []).map((version) => ({
    id: version.id,
    text: version.system_prompt,
    createdAt: version.created_at,
    label: SOURCE_LABELS[version.source] ?? version.source,
    ...describeVersion?.(version),
  }))

  return (
    <>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="gap-1.5"
        onClick={() => setOpen(true)}
      >
        <History className="w-3.5 h-3.5" />
        <span>History</span>
      </Button>
      <PromptHistoryDialog
        open={open}
        onOpenChange={setOpen}
        entries={entries}
        isLoading={versionsQuery.isLoading}
        error={versionsQuery.isError ? versionsQuery.error : null}
        currentText={currentPrompt}
        onRestore={onRestore}
        canRestore={canRestore}
      />
    </>
  )
}
